import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';
import { CardState } from '../common';
import { RootState } from '../store/rootReducer';
import { api } from '../api/api';
import { SearchItem } from '../cards/search/search.slice';

interface MemorizeState {
  cards: SearchItem[];
  selectedCardIds: string[];
  currentCardId: string | null;
  displayState: 'question' | 'answer';
  // Bumped whenever a new card is presented, so multicards re-pick a random
  // subcard even when the same card stays current (e.g. only one selected).
  subcardTurn: number;
  errorCounts: Record<string, number>;
  isLoading: boolean;
  error: string | null;
}

const initialState: MemorizeState = {
  cards: [],
  selectedCardIds: [],
  currentCardId: null,
  displayState: 'question',
  subcardTurn: 0,
  errorCounts: {},
  isLoading: false,
  error: null,
};

const memorizeSlice = createSlice({
  name: 'memorize',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setCards: (state, action: PayloadAction<SearchItem[]>) => {
      state.cards = action.payload;
    },
    toggleCardSelection: (state, action: PayloadAction<string>) => {
      const cardId = action.payload;
      const index = state.selectedCardIds.indexOf(cardId);
      
      if (index >= 0) {
        state.selectedCardIds.splice(index, 1);
        // If we deselected the current card, pick a new one
        if (state.currentCardId === cardId) {
          state.currentCardId = getRandomCardId(state.selectedCardIds, state.errorCounts);
        }
      } else {
        state.selectedCardIds.push(cardId);
        // If no current card, set this as current
        if (state.currentCardId === null || state.currentCardId === undefined || state.currentCardId === '') {
          state.currentCardId = cardId;
        }
      }
    },
    selectAllCards: (state) => {
      state.selectedCardIds = state.cards.map(c => c.id);
      if ((state.currentCardId === null || state.currentCardId === undefined || state.currentCardId === '') && state.cards.length > 0) {
        state.currentCardId = getRandomCardId(state.selectedCardIds, state.errorCounts);
      }
    },
    deselectAllCards: (state) => {
      state.selectedCardIds = [];
      state.currentCardId = null;
    },
    setCurrentCard: (state, action: PayloadAction<string | null>) => {
      state.currentCardId = action.payload;
      state.displayState = 'question';
      state.subcardTurn += 1;
    },
    setDisplayState: (state, action: PayloadAction<'question' | 'answer'>) => {
      state.displayState = action.payload;
    },
    incrementErrorCount: (state, action: PayloadAction<string>) => {
      const cardId = action.payload;
      state.errorCounts[cardId] = (state.errorCounts[cardId] || 0) + 1;
    },
    showNextCard: (state) => {
      if (state.selectedCardIds.length > 0) {
        const newCardId = getRandomCardId(state.selectedCardIds, state.errorCounts, state.currentCardId);
        state.currentCardId = newCardId;
        state.displayState = 'question';
        state.subcardTurn += 1;
      }
    },
    resetSession: (state) => {
      state.errorCounts = {};
      state.displayState = 'question';
      state.subcardTurn += 1;
      if (state.selectedCardIds.length > 0) {
        state.currentCardId = getRandomCardId(state.selectedCardIds, {});
      }
    },
  },
});

// Helper function to get random card with weighted selection based on errors
function getRandomCardId(
  selectedIds: string[], 
  errorCounts: Record<string, number>,
  currentCardId?: string | null
): string | null {
  if (selectedIds.length === 0) return null;
  
  // If we have more than one card, try to avoid showing the same card twice in a row
  let availableCardIds = selectedIds;
  if (selectedIds.length > 1 && currentCardId !== null && currentCardId !== undefined && currentCardId !== '') {
    availableCardIds = selectedIds.filter(id => id !== currentCardId);
  }
  
  // If filtering left us with no cards (shouldn't happen), use all cards
  if (availableCardIds.length === 0) {
    availableCardIds = selectedIds;
  }
  
  // Create weighted array - cards with more errors appear more frequently
  const weightedCards: string[] = [];
  availableCardIds.forEach(id => {
    const weight = Math.max(1, (errorCounts[id] || 0) + 1);
    for (let i = 0; i < weight; i++) {
      weightedCards.push(id);
    }
  });
  
  const randomIndex = Math.floor(Math.random() * weightedCards.length);
  return weightedCards[randomIndex];
}

export const {
  setLoading,
  setError,
  setCards,
  toggleCardSelection,
  selectAllCards,
  deselectAllCards,
  setCurrentCard,
  setDisplayState,
  incrementErrorCount,
  showNextCard,
  resetSession,
} = memorizeSlice.actions;

// Thunk to load memorizer cards
export const loadMemorizerCards = () => async (dispatch: Dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  
  try {
    // Directly call API to get all cards in Memorizer state
    const cards = await api.search({
      text: '',
      tag: '',
      state: CardState.Memorizer,
    }) as unknown as SearchItem[];
    dispatch(setCards(cards));
  } catch {
    dispatch(setError('Failed to load memorizer cards'));
  } finally {
    dispatch(setLoading(false));
  }
};

// Selectors
export const selectMemorizeState = (state: RootState) => state.memorize;
export const selectSelectedCards = (state: RootState) => {
  const memorizeState = state.memorize;
  return memorizeState.cards.filter((card: SearchItem) => 
    memorizeState.selectedCardIds.includes(card.id)
  );
};
export const selectCurrentCardId = (state: RootState) => {
  return state.memorize.currentCardId;
};

export default memorizeSlice.reducer;